import {
  createTheme,
  virtualColor,
  type CheckboxProps,
  type MantineColorShade,
} from "@mantine/core";
import { generateColors } from "@mantine/colors-generator";
import { palette, fonts, transition } from "@/ui/tokens";
import type { BrandingConfig } from "@verity/shared-types";

const DRAWER_TRANSITION = {
  duration: 280,
  timingFunction: transition.drawer.replace("280ms ", ""),
};
const DRAWER_OVERLAY_OPACITY = 0.35;

// Verity's own brand blue (palette.blue[6]). When the resolved branding carries
// this color we treat it as "the Verity default" and return the byte-identical
// static theme below — the live Verity look must not shift as a side effect of
// the white-label refactor. Real partners get a generated tuple (see
// buildThemeFromBranding).
const VERITY_BRAND_BLUE = palette.blue[6].toLowerCase(); // "#3455d3"

// Switch knob glyphs. Both ship on every switch and `.verity-switch-glyph-*`
// in verity-theme.css hides the one that does not apply — see the Switch entry
// in `components` below for why this cannot be done from the React side.
// The check is the same path the Checkbox draws, so a checked box and an on
// switch carry one mark between them.
function VeritySwitchThumbIcon() {
  return (
    <>
      <svg
        className="verity-switch-glyph verity-switch-glyph-on"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 7.5l2.6 2.5L11 4.5"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <svg
        className="verity-switch-glyph verity-switch-glyph-off"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4.2 4.2l5.6 5.6M9.8 4.2l-5.6 5.6"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      </svg>
    </>
  );
}

// Checkbox glyph. Mantine's default is a 10×7 filled check; ours is a square
// 14×14 stroke check — the same path the report's section-selection badge
// draws, so the two selection idioms read as one mark. It was introduced for
// the round control it centres optically in; the control went square on
// 2026-08-27 and the glyph stayed, because as a stroke on a white ground it is
// the lighter, cleaner of the two. The indeterminate dash is Mantine's.
function VerityCheckboxIcon({
  indeterminate,
  className,
}: CheckboxProps["icon"] extends React.FC<infer P> ? P : never) {
  if (indeterminate) {
    return (
      <svg
        className={className}
        viewBox="0 0 32 6"
        fill="none"
        aria-hidden
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="32" height="6" fill="currentColor" rx="3" />
      </svg>
    );
  }
  return (
    <svg
      className={className}
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 7.5l2.6 2.5L11 4.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// The theme options are extracted into a reusable object so the per-partner
// builder can spread them and override ONLY `colors.blue` + `primaryColor`.
const baseThemeOptions = {
  colors: {
    "app-gray": palette.gray,
    "app-blue": palette.blue,
    "dark-gray": palette.darkGray,
    "app-error": palette.error,
    "app-success": palette.success,
    "app-warning": palette.warning,
    surface: virtualColor({
      name: "surface",
      dark: "dark-gray",
      light: "app-gray",
    }),
  },
  fontFamily: fonts.body,
  fontFamilyMonospace: fonts.mono,
  headings: {
    fontFamily: fonts.heading,
  },
  components: {
    // ── Inputs ──────────────────────────────────────────────────────────────
    TextInput: {
      classNames: { input: "verity-input" },
    },
    PasswordInput: {
      classNames: { input: "verity-input" },
    },
    Textarea: {
      classNames: { input: "verity-input" },
    },
    NumberInput: {
      classNames: { input: "verity-input" },
    },
    // Autocomplete was missing here entirely, so its input AND its dropdown
    // rendered as bare Mantine while every sibling field carried the house
    // treatment.
    Autocomplete: {
      classNames: { input: "verity-input", dropdown: "verity-dropdown" },
    },
    Select: {
      classNames: { input: "verity-input", dropdown: "verity-dropdown" },
    },
    TagsInput: {
      classNames: { input: "verity-input", dropdown: "verity-dropdown" },
    },
    InputWrapper: {
      defaultProps: {
        inputWrapperOrder: ["label", "input", "description", "error"],
      },
      classNames: {
        label: "verity-input-label",
        description: "verity-input-description",
      },
    },
    Combobox: {
      classNames: { option: "verity-combobox-option" },
    },
    // The knob carries the state as a glyph: a check when on, a cross when off
    // (2026-08-27, chosen from the /toggle-lab bake-off). It is the only
    // treatment where the state survives without colour — grayscale, colour
    // blindness, a washed-out projector — instead of resting entirely on "blue
    // means on".
    //
    // BOTH glyphs are always in the DOM and CSS hides the wrong one, which
    // looks roundabout until you try the obvious thing: `thumbIcon` is a static
    // node, and `defaultProps` is evaluated once with no idea whether any given
    // switch is checked. There is no per-state hook to hang it on, so the state
    // has to be read where it lives — off the input's `:checked`, in CSS.
    //
    // Deliberately NOT setting `size`: the bake-off showed this at `md`, but 19
    // callsites pin `xs` or `sm`, and a global `md` would only apply to the
    // ones that don't — a bigger mix than we started with. The glyph is sized
    // as a percentage of the knob instead, so it holds at every size.
    Switch: {
      defaultProps: {
        withThumbIndicator: false,
        thumbIcon: <VeritySwitchThumbIcon />,
      },
      classNames: {
        input: "verity-switch-input",
        track: "verity-switch-track",
        thumb: "verity-switch-thumb",
      },
    },

    // Checked reads as a blue check in a blue-outlined square, not a filled
    // blue tile (2026-08-27, chosen from the /checkbox-lab bake-off). Mantine's
    // own `outline` variant already means exactly that — border AND check take
    // `--mantine-color-<primary>-outline`, so a white-label partner's colour
    // carries through without a hardcoded blue anywhere. The one thing outline
    // gets wrong for us is that it drops the checked box to `transparent`,
    // which makes a checked box on a tinted surface look sunken next to its
    // unchecked neighbours; `.verity-checkbox-input` pins the fill back to the
    // unchecked one so only the border and the mark change.
    // Size and the glyph stay as they were, so no layout shifts.
    Checkbox: {
      defaultProps: { radius: 4, variant: "outline", icon: VerityCheckboxIcon },
      classNames: { input: "verity-checkbox-input" },
    },
    // The same mark without an <input>, for rows that are themselves the
    // control (the shared MultiSelect's options): one checkbox look app-wide.
    CheckboxIndicator: {
      defaultProps: { radius: 4, variant: "outline", icon: VerityCheckboxIcon },
      classNames: { indicator: "verity-checkbox-indicator" },
    },

    // ── Button & ActionIcon ─────────────────────────────────────────────────
    Button: {
      classNames: { root: "verity-btn" },
    },
    ActionIcon: {
      defaultProps: { variant: "subtle" },
      classNames: { root: "verity-action-icon" },
    },

    // ── Card ────────────────────────────────────────────────────────────────
    Card: {
      defaultProps: { radius: "md" },
      classNames: { root: "verity-card" },
    },

    // ── Badge ───────────────────────────────────────────────────────────────
    Badge: {
      defaultProps: { variant: "light" },
    },

    // ── Modal ───────────────────────────────────────────────────────────────
    Modal: {
      classNames: { content: "verity-modal-content" },
    },

    // ── Drawer ──────────────────────────────────────────────────────────────
    // Three keys, one look. Mantine resolves theme defaults per component
    // NAME: the plain `<Drawer>` reads "Drawer", but `<Drawer.Root>` reads
    // "DrawerRoot" and `<Drawer.Overlay>` reads "DrawerOverlay"
    // (@mantine/core DrawerRoot.mjs / DrawerOverlay.mjs `useProps`). A
    // compound drawer (GitHub connection, Create report) would otherwise open
    // with Mantine's own transition and overlay instead of ours.
    Drawer: {
      defaultProps: {
        transitionProps: DRAWER_TRANSITION,
        overlayProps: { backgroundOpacity: DRAWER_OVERLAY_OPACITY },
      },
    },
    DrawerRoot: {
      defaultProps: { transitionProps: DRAWER_TRANSITION },
    },
    DrawerOverlay: {
      defaultProps: { backgroundOpacity: DRAWER_OVERLAY_OPACITY },
    },

    // ── Navigation & Layout ─────────────────────────────────────────────────
    Tabs: {
      classNames: { tab: "verity-tab" },
    },
    Menu: {
      classNames: { dropdown: "verity-menu-dropdown", item: "verity-menu-item" },
    },
    // Popover dropdowns get the same elevation as Menu's — the shared
    // `--popover-shadow` default lives in verity-theme.css. HoverCard is left
    // out on purpose: its single usage passes `shadow` itself.
    Popover: {
      classNames: { dropdown: "verity-popover-dropdown" },
    },
    NavLink: {
      classNames: { root: "verity-navlink" },
    },

    // ── Data Display ────────────────────────────────────────────────────────
    Table: {
      classNames: { tr: "verity-table-row" },
    },
    Tooltip: {
      classNames: { tooltip: "verity-tooltip" },
    },
    Alert: {
      classNames: { root: "verity-alert" },
    },
    Notification: {
      classNames: { root: "verity-notification" },
    },

    // ── Misc ────────────────────────────────────────────────────────────────
    Divider: {
      classNames: { root: "verity-divider" },
    },
    SegmentedControl: {
      classNames: {
        root: "verity-segmented-root",
        indicator: "verity-segmented-indicator",
        label: "verity-segmented-label",
      },
    },
  },
} as const;

/**
 * Static Verity default theme — the exact theme the app has always shipped.
 * Kept as the default export so any module that imports `theme` (and the
 * BrandingProvider's Verity-default / loading path) renders identically to
 * today. Note: `primaryColor` is intentionally NOT set here, so Mantine's
 * built-in `blue` (#228be6) drives implicit-primary components — matching the
 * pre-white-label behaviour byte-for-byte.
 */
export const theme = createTheme(baseThemeOptions);

/**
 * Build a Mantine theme from a partner's resolved branding.
 *
 * - **Verity default** (branding is null, or `primaryColor` is the Verity brand
 *   blue): returns the static `theme` unchanged — zero visual drift for Verity.
 * - **Real partner**: expands `branding.primaryColor` into a 10-shade tuple and
 *   overrides the `blue` color key with it AND sets `primaryColor: "blue"`.
 *   That single override recolors every accent surface at once — the 120+
 *   explicit `color="blue"` / `c="blue"` usages AND all implicit-primary
 *   components (filled buttons, checkboxes, switches, loaders, focus rings) —
 *   so a partner's colour applies across the whole UI, not just a subset.
 *
 * The hand-tuned `app-blue` tuple and every other color key are preserved, so
 * components that opt into `color="app-blue"` keep Verity blue by design.
 */
export function buildThemeFromBranding(branding?: BrandingConfig | null) {
  const color = branding?.primaryColor?.trim().toLowerCase();
  if (!color || color === VERITY_BRAND_BLUE) {
    return theme;
  }
  const partnerShades = generateColors(branding!.primaryColor);
  // generateColors anchors the input at the shade closest to its lightness —
  // NOT always index 6 (a dark teal like #0d8f83 lands on 9, an orange on 7).
  // Use that anchor index as primaryShade so the primary is the partner's EXACT
  // colour, not a lighter/darker generated shade.
  const anchor = partnerShades.findIndex((c) => c.toLowerCase() === color);
  const primaryShade = (anchor >= 0 ? anchor : 6) as MantineColorShade;
  return createTheme({
    ...baseThemeOptions,
    colors: {
      ...baseThemeOptions.colors,
      blue: partnerShades,
    },
    primaryColor: "blue",
    primaryShade,
  });
}
